import { useState } from "react";
import { useNavigate, useSearchParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, MailCheck } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { TenantShell, BrandButton, inputCls, labelCls } from "../../components/tenant/TenantShell";
import { tapi, setTenantToken, apiErr, getSectorFit } from "../../lib/tenant";
import { loadPrequal } from "../../lib/lpoPrequal";
import { trackSignUp } from "../../lib/ga";

export default function TenantAuth() {
  const { sub, config, refreshMe, to } = useTenant();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "signup");
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", business_name: "" });
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // { email } — check-your-inbox state
  const [resend, setResend] = useState("idle");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const eco = config.partner_type === "ecosystem";

  // Ecosystem partners ask the sector question before sign-up.
  if (eco && mode === "signup" && getSectorFit(sub) !== "yes") {
    return <Navigate to={to("/fit")} replace />;
  }
  // Pre-qualification tenants (e.g. Lasiri LPO) must pass the eligibility check before sign-up.
  const prequal = config.prequal_flow ? loadPrequal(sub) : null;
  if (config.prequal_flow && mode === "signup" && !prequal?.passed) {
    return <Navigate to={to("/prequalify")} replace />;
  }

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = mode === "signup"
        ? (eco ? { name: f.name, email: f.email, password: f.password, business_name: f.business_name } : { name: f.name, email: f.email, password: f.password, phone: f.phone, ...(prequal?.prequal_id ? { prequal_id: prequal.prequal_id } : {}) })
        : { email: f.email, password: f.password };
      const { data } = await tapi.post(`/t/${sub}/auth/${mode}`, body);
      if (mode === "signup") trackSignUp("email", { source: "tenant_portal", tenant: sub });
      if (data.verification_required) { setPending({ email: data.email }); setBusy(false); return; }
      setTenantToken(data.token);
      await refreshMe();
      toast.success(mode === "signup" ? `Welcome, ${data.name?.split(" ")[0] || "there"}!` : "Welcome back");
      navigate(to("/journey"));
    } catch (err) {
      if (err?.response?.status === 403 && err.response.data?.detail?.code === "email_unverified") { setPending({ email: err.response.data.detail.email || f.email, fromLogin: true }); }
      else toast.error(apiErr(err, "Couldn't sign you in"));
    }
    setBusy(false);
  };

  const doResend = async () => {
    setResend("sending");
    try { await tapi.post(`/t/${sub}/auth/resend-verification`, { email: pending.email }); setResend("sent"); }
    catch (_) { setResend("idle"); toast.error("Couldn't resend — try again in a moment"); }
  };

  if (pending) {
    return (
      <TenantShell>
        <div className="mx-auto max-w-md rounded-2xl border border-neutral-200 bg-white p-6 text-center shadow-sm" data-testid="tenant-auth-check-inbox">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-white" style={{ background: "var(--tenant)" }}><MailCheck size={26} /></span>
          <h1 className="mt-4 text-xl font-extrabold">{pending.fromLogin ? "Verify your email to sign in" : "Check your inbox"}</h1>
          <p className="mt-2 text-sm text-neutral-600">
            We sent a verification link to <span className="font-semibold text-neutral-900" data-testid="tenant-auth-check-inbox-email">{pending.email}</span>.
            Click it to {pending.fromLogin ? "sign in and continue" : "activate your account and continue"} your {config.name} application.
          </p>
          <p className="mt-3 text-xs text-neutral-500">The link expires in 24 hours. Can't find it? Check your spam folder.</p>
          <BrandButton type="button" onClick={doResend} disabled={resend !== "idle"} className="mt-5 w-full" data-testid="tenant-auth-resend">
            {resend === "sending" && <Loader2 size={14} className="animate-spin" />}{resend === "sent" ? "Sent — check your inbox again" : "Resend verification email"}
          </BrandButton>
          <button type="button" onClick={() => { setPending(null); setMode("login"); }} className="mt-3 text-xs font-semibold text-neutral-500 underline" data-testid="tenant-auth-back-to-login">Back to sign in</button>
        </div>
      </TenantShell>
    );
  }

  return (
    <TenantShell>
      <div className="mx-auto max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="tenant-auth-card">
        <div className="mb-5 flex rounded-full bg-neutral-100 p-1 text-xs font-bold">
          {["signup", "login"].map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)} data-testid={`tenant-auth-tab-${m}`}
              className={"flex-1 rounded-full py-2 transition-colors " + (mode === m ? "bg-white shadow-sm text-neutral-900" : "text-neutral-500")}>
              {m === "signup" ? "Create account" : "Sign in"}
            </button>
          ))}
        </div>
        <h1 className="text-xl font-extrabold">{mode === "signup" ? (eco ? "Create your account" : "Start your assessment") : "Welcome back"}</h1>
        <p className="mt-1 text-sm text-neutral-500">Your {config.name} {eco ? "" : "assessment "}account is separate from any Clinq account you may already have.</p>
        <form onSubmit={submit} className="mt-5 space-y-4">
          {mode === "signup" && (
            <label className="block"><span className={labelCls}>Full name</span>
              <input required value={f.name} onChange={set("name")} className={inputCls} placeholder="Jane Wanjiru" data-testid="tenant-auth-name" /></label>
          )}
          <label className="block"><span className={labelCls}>Email address</span>
            <input required type="email" value={f.email} onChange={set("email")} className={inputCls} placeholder="you@business.co.ke" data-testid="tenant-auth-email" /></label>
          {mode === "signup" && eco && (
            <label className="block"><span className={labelCls}>Business name</span>
              <input required value={f.business_name} onChange={set("business_name")} className={inputCls} placeholder="e.g. SolarGrid Ltd" data-testid="tenant-auth-business-name" /></label>
          )}
          {mode === "signup" && !eco && (
            <label className="block"><span className={labelCls}>Phone (optional)</span>
              <input value={f.phone} onChange={set("phone")} className={inputCls} placeholder="+254 7xx xxx xxx" data-testid="tenant-auth-phone" /></label>
          )}
          <label className="block"><span className={labelCls}>Password</span>
            <input required type="password" minLength={8} value={f.password} onChange={set("password")} className={inputCls} placeholder="At least 8 characters" data-testid="tenant-auth-password" /></label>
          <BrandButton type="submit" disabled={busy} className="w-full" data-testid="tenant-auth-submit">
            {busy && <Loader2 size={14} className="animate-spin" />}{mode === "signup" ? "Create account & continue" : "Sign in"}
          </BrandButton>
        </form>
        <p className="mt-4 text-center text-[11px] text-neutral-400">{config.name} Relationship Manager? <Link to={to("/rm")} className="font-semibold underline" data-testid="tenant-auth-rm-link">Sign in to the RM portal</Link></p>
      </div>
    </TenantShell>
  );
}
